import 'react-loading-skeleton/dist/skeleton.css';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfiguratorState, useUrlSync, useVehicleSync } from './store';
import { useBindValidator } from './validate';
import { BrandSelector } from './steps/BrandSelector';
import { ModelSelector } from './steps/ModelSelector';
import { Customizer } from './steps/Customizer';
import { SummaryPage } from './steps/Summary';
import { ConfiguratorStepHeader } from './ConfiguratorStepHeader';
import { useProgressPersistence, useResumePrompt } from './persistence';
import { ResumePromptModal } from './steps/ResumePromptModal';
import { useConfigurationRestore } from './savedConfigurations/restore';
import { UnavailableConfigurationModal } from './savedConfigurations/UnavailableConfigurationModal';
import { useConfiguratorTranslations } from './translations';
import styles from './configurator.module.scss';

export default function ConfiguratorPage() {
	useUrlSync();
	useBindValidator();
	useVehicleSync();
	useProgressPersistence();
	const resumePrompt = useResumePrompt();
	const restore = useConfigurationRestore();
	const navigate = useNavigate();
	const step = useConfiguratorState((s) => s.step);
	const subsection = useConfiguratorState((s) => s.subsection);
	const tab = useConfiguratorState((s) => s.tab);
	const tr = useConfiguratorTranslations();
	const shellRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let el: HTMLElement | null = shellRef.current?.parentElement ?? null;
		while (el) {
			const overflowY = getComputedStyle(el).overflowY;
			if (overflowY === 'auto' || overflowY === 'scroll') {
				el.scrollTo({ top: 0, behavior: 'smooth' });
				return;
			}
			el = el.parentElement;
		}
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [step, subsection, tab]);

	return (
		<>
			{resumePrompt.open && (
				<ResumePromptModal
					saved={resumePrompt.saved}
					onContinue={resumePrompt.onContinue}
					onStartClean={resumePrompt.onStartClean}
				/>
			)}
			{restore.reason && restore.record && (
				<UnavailableConfigurationModal
					reason={restore.reason}
					record={restore.record}
					onClose={() => {
						restore.dismiss();
						navigate('/my-car-configurations');
					}}
					onConfigureSimilar={(listing) => {
						restore.dismiss();
						useConfiguratorState.getState().reset();
						const params = new URLSearchParams({
							step: 'model-selector',
							makeCode: listing.makeCode,
							make: listing.makeLabel,
							modelCode: listing.modelCode,
							model: listing.modelLabel,
						});
						navigate(`/car-configurator?${params}`);
					}}
				/>
			)}
			<div className={styles.shell} ref={shellRef}>
				<header className={styles.header}>
					<div className={styles.headerInner}>
						<span className={styles.brand}>{tr.title}</span>
						<ConfiguratorStepHeader />
						<span />
					</div>
				</header>
				<main className={styles.main}>
					{step === 'brand-selector' && <BrandSelector />}
					{step === 'model-selector' && <ModelSelector />}
					{step === 'customizer' && <Customizer />}
					{step === 'summary' && <SummaryPage />}
				</main>
			</div>
		</>
	);
}
